import { FlexRow } from '@/components/common/Flex';
import { useErrorToast } from '@/components/common/Toasts';
import { Paragraph } from '@/components/common/Typography';
import { logError } from '@/util/logger';
import { useLazyQuery, useMutation } from '@apollo/client';
import { ButtonProps } from '@chakra-ui/react';
import {
    ApplyPromotionCodeDocument,
    CreateMembershipDocument,
    CurrentUserProfileDocument,
    PromotionCodeFieldsFragment,
    StripeSetupIntentClientSecretDocument,
} from '@common/graphql/generated';
import { TOASTS } from '@common/toasts';
import { sendGTMEvent } from '@next/third-parties/google';
import {
    PaymentElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';
import React, { useState } from 'react';
import { Button } from '../common/Button';
import ErrorMessage from '../common/ErrorMessage';
import { FlexCol } from '../common/Flex';

export interface PaymentFormProps {
    onComplete: VoidFunction;
    currentMembershipId?: string;
    buttonProps?: ButtonProps;
    buttonText?: string;
    promotionCode?: PromotionCodeFieldsFragment | null;
}

export default function PaymentForm({
    onComplete,
    currentMembershipId,
    buttonText = 'Update payment information',
    buttonProps,
    promotionCode,
}: PaymentFormProps) {
    const stripe = useStripe();
    const elements = useElements();
    const [createMembership] = useMutation(CreateMembershipDocument, {
        refetchQueries: [CurrentUserProfileDocument],
        awaitRefetchQueries: true,
    });
    const [applyPromotionCode] = useMutation(ApplyPromotionCodeDocument);
    const [stripeSetupIntentClientSecret] = useLazyQuery(
        StripeSetupIntentClientSecretDocument,
        { fetchPolicy: 'no-cache' }
    );
    const [errorMessage, setErrorMessage] = useState<string>();
    const [loading, setLoading] = useState(false);
    const showErrorToast = useErrorToast();

    const handleStripeError = (error: StripeError) => {
        setLoading(false);
        setErrorMessage(error.message);
    };

    const handleCreateMembershipError = () => {
        setLoading(false);
        setErrorMessage('Failed to prepare subscription');
    };

    const handleSetupIntentClientSecretError = () => {
        setLoading(false);
        setErrorMessage('Failed to update payment method');
    };

    const handleSubmit = async () => {
        setLoading(true);

        if (!stripe || !elements) {
            // Stripe.js hasn't yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            setLoading(false);
            return;
        }

        // Trigger form validation and wallet collection
        const { error: submitError } = await elements.submit();
        if (submitError) {
            handleStripeError(submitError);
            return;
        }

        let clientSecret: string;
        let clientSecretType: string;

        if (!currentMembershipId) {
            // Create Subscription
            const { data } = await createMembership(
                promotionCode
                    ? {
                          variables: {
                              promotionCode: promotionCode.code,
                          },
                      }
                    : {}
            );
            if (
                !data?.createMembership?.clientSecret ||
                !data?.createMembership?.clientSecretType
            ) {
                handleCreateMembershipError();
                return;
            }
            clientSecret = data.createMembership.clientSecret;
            clientSecretType = data.createMembership.clientSecretType;
        } else {
            // Create SetupIntent to update default payment method
            const { data } = await stripeSetupIntentClientSecret({
                variables: { membershipId: currentMembershipId },
            });
            if (!data) {
                handleSetupIntentClientSecretError();
                return;
            }
            clientSecret = data.stripeSetupIntentClientSecret;
            clientSecretType = 'setup_intent';
        }

        // Confirm using the details collected by the Payment Element
        const confirmation = {
            elements,
            clientSecret,
            confirmParams: {
                return_url: window.location.href,
            },
            redirect: 'if_required' as const,
        };

        const { error } =
            clientSecretType === 'setup_intent'
                ? await stripe.confirmSetup(confirmation)
                : await stripe.confirmPayment(confirmation);

        if (error) {
            // This point is only reached if there's an immediate error when
            // confirming the payment. Show the error to your customer (for example, payment details incomplete)
            handleStripeError(error);
        } else {
            // Apply promotion code to existing membership
            if (currentMembershipId && promotionCode) {
                try {
                    await applyPromotionCode({
                        variables: { id: promotionCode.id },
                    });
                } catch (e) {
                    showErrorToast(TOASTS.applyPromotionCodeError);
                    logError(e);
                }
            }

            sendGTMEvent({
                event: 'free_trial_signup',
                trigger: 'GAevent',
            });

            // Your customer might be redirected to your `return_url`. For some payment
            // methods like iDEAL, your customer is redirected to an intermediate
            // site first to authorize the payment, then redirected to the `return_url`.
            setLoading(false);

            onComplete();
        }
    };

    return (
        <FlexCol flex={1}>
            <PaymentElement />
            <Button
                disabled={!stripe || !elements || loading}
                my={6}
                isLoading={loading}
                w="full"
                onClick={handleSubmit}
                onClickEvent="Submit Payment Button Clicked"
                onClickEventParams={{
                    label: buttonText,
                }}
                {...buttonProps}
            >
                {buttonText}
            </Button>
            <Paragraph color="gray.500" fontSize="sm">
                We will email you before you ever get charged, and you can
                cancel anytime during the 30-day free trial.
            </Paragraph>
            {errorMessage && (
                <FlexRow>
                    <ErrorMessage message={errorMessage} />
                </FlexRow>
            )}
        </FlexCol>
    );
}
